import React, { useEffect, useState } from 'react';
import { downloadBlob, fetchSavedVerification, verifySavedFile } from '../services/api';
import { Button, ErrorText, Loading, errorMessage } from './ui';

// Signing off a saved answer or report.
//
// The wording here is doing real work, so it is deliberate throughout.
//
// What the person is asserting is shown as editable text rather than implied
// by a button, because a signature whose meaning is written in a tooltip is
// not one. The default sentence says what it actually is - reviewed against
// the records it cites and accepted - and nothing on this screen says
// "compliant", "certified" or "approved for construction": the app records who
// checked what and when, and what that means belongs to the regime it is
// produced for, not to a button label.
//
// The name is never collected here. It comes from the signed-in session on the
// server, because a name typed into a form is a claim about a person rather
// than an act by one.
export default function VerifySavedFile({ blobName, title, onVerified }) {
  const [state, setState] = useState(null);
  const [statement, setStatement] = useState(
    'I have reviewed this document against the project records it cites and accept it.',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    fetchSavedVerification(blobName)
      .then((data) => { if (live) setState(data || {}); })
      .catch((e) => { if (live) { setError(errorMessage(e)); setState({}); } });
    return () => { live = false; };
  }, [blobName]);

  async function sign() {
    setBusy(true);
    setError('');
    try {
      const res = await verifySavedFile({ blobName, statement });
      setState({ verified: true, verification: res?.verification || null, note: res?.note || '' });
      onVerified?.(res);
    } catch (e) {
      setError(errorMessage(e));
      // A refused second signature is not a failure to report and forget: the
      // existing one is the answer, so show it.
      try {
        const existing = await fetchSavedVerification(blobName);
        if (existing?.verified) setState(existing);
      } catch { /* the error above stands */ }
    } finally {
      setBusy(false);
    }
  }

  if (state === null) return <div className="saved-answer-body"><Loading text="Checking…" /></div>;

  const v = state.verification;

  return (
    <div className="saved-answer-body">
      <ErrorText>{error}</ErrorText>

      {v ? (
        <>
          <p className="saved" style={{ marginTop: 0 }}>
            Verified by {v.verifiedBy}
            {v.verifiedAt ? ` on ${new Date(v.verifiedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}.
          </p>
          {v.statement ? <p className="card-hint" style={{ marginTop: 0 }}>“{v.statement}”</p> : null}
          <p className="card-hint">
            {v.model ? <>Produced by {v.model}. </> : null}
            {v.sourceCount ? <>{v.sourceCount} source{v.sourceCount === 1 ? '' : 's'}. </> : null}
            {v.verifiedFile ? <>A signed copy is what gets emailed or sent to Procore.</> : null}
          </p>
          {/* The signed copy is deliberately not a row in the list - it is an
              artifact of this answer, not another answer - so this is the way
              to it. Downloading the signed one matters: it is the only copy
              that carries the signature page. */}
          {v.verifiedFile ? (
            <Button
              compact
              secondary
              onClick={() => downloadBlob(v.verifiedFile, `${title || 'document'} (signed).pdf`)}
            >
              Download signed copy
            </Button>
          ) : null}
          {/* The check that gives the signature its value: the recorded hash
              against the file as it is now. Said loudly, because a
              verification that no longer describes the document it is attached
              to is worse than none - it lends it credibility it has lost. */}
          {v.fileChanged === true ? (
            <ErrorText>
              This document has changed since it was verified. The sign-off above describes a
              different file and should not be relied on.
            </ErrorText>
          ) : null}
          {v.fileChanged === null ? (
            <p className="card-hint">The file could not be re-checked against the signed version just now.</p>
          ) : null}
          {state.note === 'no_signed_copy_for_this_format' ? (
            <p className="card-hint">
              This one was saved as Word, so there is no signed copy — the sign-off is recorded here but
              will not appear on the file itself if you email it.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <p className="card-hint" style={{ marginTop: 0 }}>
            Signing records your name, the date, the model that produced it, the sources it cites and a
            checksum of this exact file. A signed PDF copy is produced, and that is what gets emailed or
            sent to Procore.
          </p>
          <textarea
            className="input"
            rows={3}
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            aria-label="What you are confirming"
          />
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <Button compact disabled={busy || !statement.trim()} onClick={sign}>
              {busy ? 'Signing…' : 'Verify'}
            </Button>
            <span className="card-hint">{title ? `Signs “${title}”.` : ''}</span>
          </div>
        </>
      )}
    </div>
  );
}
